/* eslint-disable max-len */
import { useFormikContext } from 'formik';
import { useAppSelector } from '../../../../redux/hooks';
import {
  selectNonPublicStudentFormViewModel,
  selectHasSearchMatch, selectAllowEdit,
} from '../../../../redux/slices/nonPublicStudentSlice';
import { NonPublicStudentEntryModel } from '../../../../types/NonPublicStudentEntryModel';
import { NonPublicStudentFormViewModel } from '../../../../types/NonPublicStudentFormViewModel';
import { PanelType } from '../../../../types/PanelType';
import { FormRadioListOrientation } from '../../../../types/propTypes/FormRadioListPropTypes';
import { NPSE_SPEDOnlyValidationSchema } from '../../../../types/Validation/NonPublicStudentEntryValidationSchema';
import {
  buildEducationEnvironmentOptions,
  isEducationEnvironmentDisabled,
} from '../../../../utilities/manualInputUtilities';
import { isStateNonAdmin } from '../../../../utilities/userUtilities';
import FormDateSelector from '../../../FormInputs/FormDateSelector/FormDateSelector';
import FormDropdown from '../../../FormInputs/FormDropdown/FormDropdown';
import FormRadioList from '../../../FormInputs/FormRadioList/FormRadioList';
import Panel from '../../../Panel/Panel';
import './NPSE_SPEDPanel.css';
import { MAX_DATE } from 'src/types/Constants';

const NPSE_SPEDPanel   : React.FC = () => {
  const { values } =
    useFormikContext<NonPublicStudentEntryModel>();

  const formViewModel: NonPublicStudentFormViewModel = useAppSelector(
    selectNonPublicStudentFormViewModel
  );

  const hasSearchMatch: boolean = useAppSelector(
    selectHasSearchMatch
  );

  const allowEdit: boolean = useAppSelector(
    selectAllowEdit
  )

  const areFormComponentsDisabled: boolean =
    !allowEdit || !hasSearchMatch || isStateNonAdmin();

  const isProjectedDiplomaRequired = (): boolean => {
    return ['09', '10', '11', '12', 'AE'].includes(values.gradeLevel);
  };

  return (
    <Panel panelType={PanelType.INFO} heading="Special Education">
      <div className="field-row field-row-sped-row-start">
        <FormDateSelector
          displayName="Eligibility Date"
          field="eligibilityDeterminationDate"
          disabled={areFormComponentsDisabled}
          validationSchema={NPSE_SPEDOnlyValidationSchema}
          maxDate={MAX_DATE}
          isClearable
        />
        <FormDateSelector
          displayName="IEP Start Date"
          field="iepStartDate"
          disabled={areFormComponentsDisabled}
          validationSchema={NPSE_SPEDOnlyValidationSchema}
          maxDate={MAX_DATE}
          isClearable
        />
        <FormDropdown
          displayName="Projected HS Diploma Type"
          field="projectedHSDiplomaType"
          options={formViewModel.projectedHSDiplomaTypeOptions}
          validationSchema={NPSE_SPEDOnlyValidationSchema}
          disabled={areFormComponentsDisabled}
          classConfig={{
            labelClasses: isProjectedDiplomaRequired()
              ? 'forced-required-label'
              : '',
          }}
          isClearable={true}
        />
        <FormDateSelector
          displayName="Services Exit Date"
          field="programParticipationExitDate"
          disabled={areFormComponentsDisabled}
          validationSchema={NPSE_SPEDOnlyValidationSchema}
          maxDate={MAX_DATE}
          isClearable
        />
      </div>

      <div className="field-row field-row-sped-alt-rows">
        <FormDropdown
          displayName="Primary Disability"
          field="primaryDisabilityType"
          options={formViewModel.primaryDisabilityTypeOptions}
          disabled={areFormComponentsDisabled}
          validationSchema={NPSE_SPEDOnlyValidationSchema}
        />
        <FormDropdown
          displayName="Secondary Disability"
          field="secondaryDisabilityType"
          options={formViewModel.secondaryDisabilityTypeOptions}
          disabled={areFormComponentsDisabled}
          validationSchema={NPSE_SPEDOnlyValidationSchema}
          isClearable={true}
        />
        <FormRadioList
          displayName="SCD"
          field="significantCognitiveDisabilityIndicator"
          optionOrientation={FormRadioListOrientation.Horizontal}
          options={formViewModel.significantCognitiveDisabilityIndicatorOptions}
          disabled={areFormComponentsDisabled}
          validationSchema={NPSE_SPEDOnlyValidationSchema}
        />
      </div>

      <div className="field-row field-row-sped-alt-rows">
        <FormDropdown
          displayName="Education Environment"
          field="ideaEducationalEnvironment"
          options={buildEducationEnvironmentOptions(
            formViewModel.ideaEducationalEnvironments,
            values.ageEvaluationYear,
            values.birthDate,
            values.gradeLevel
          )}
          disabled={
            areFormComponentsDisabled ||
            isStateNonAdmin() ||
            isEducationEnvironmentDisabled(
              values.ageEvaluationYear,
              values.birthDate
            )
          }
          classConfig={{
            labelClasses: !isEducationEnvironmentDisabled(
              values.ageEvaluationYear,
              values.birthDate
            )
              ? 'forced-required-label'
              : '',
          }}
          validationSchema={NPSE_SPEDOnlyValidationSchema}
        />
        <FormDropdown
          displayName="Related Services"
          field="relatedServices"
          isMulti={true}
          options={formViewModel.relatedServicesOptions}
          disabled={areFormComponentsDisabled}
          validationSchema={NPSE_SPEDOnlyValidationSchema}
        />
      </div>
    </Panel>
  );
};

export default NPSE_SPEDPanel;
